import { TeamsOidcClient } from "~/utils/helpers/auth/oidc-client";
import { OIDC_SESSION_LOCK } from "~/utils/helpers/auth/oidc-session";
import {
  OIDC_CALLBACK_PATH,
  OIDC_LOGOUT_PATH,
  oidcAuthority,
  validateOidcConfiguration,
} from "~/utils/helpers/auth/oidc-contract";
import { authLocalStorage, authSessionStorage, isEmbeddedAuth } from "~/utils/helpers/auth/browser-context";

let client: TeamsOidcClient | undefined;
let embeddedQueue = Promise.resolve();

export function useOidcClient(): TeamsOidcClient {
  if (client) return client;
  const embedded = isEmbeddedAuth();
  if (!import.meta.client || (!embedded && !navigator.locks))
    throw new Error("Ce navigateur ne permet pas une connexion sécurisée. Utilisez un navigateur récent.");
  const config = useRuntimeConfig().public;
  const ordersUrl = String(config.ORDERS_URL).replace(/\/+$/, "");
  const origin = window.location.origin;
  const oidc = validateOidcConfiguration(
    {
      authority: oidcAuthority(config, origin),
      clientId: String(config.OIDC_CLIENT_ID ?? ""),
      resource: ordersUrl,
      redirectUri: `${origin}${OIDC_CALLBACK_PATH}`,
      postLogoutRedirectUri: `${origin}${OIDC_LOGOUT_PATH}`,
      ordersUrl,
    },
    origin,
  );
  // Force capability checks before sending the browser away with a transaction it cannot retain.
  for (const storage of [authLocalStorage(), authSessionStorage()]) {
    const key = `teams.oidc.storage-check.${crypto.randomUUID()}`;
    storage.setItem(key, key);
    storage.removeItem(key);
  }
  client = new TeamsOidcClient(
    oidc,
    authLocalStorage(),
    authSessionStorage(),
    embedded
      ? (operation) => {
          const next = embeddedQueue.then(operation);
          embeddedQueue = next.then(
            () => undefined,
            () => undefined,
          );
          return next;
        }
      : async (operation) => await navigator.locks.request(OIDC_SESSION_LOCK, operation),
    embedded,
  );
  return client;
}

export function navigateOidc(url: string): void {
  if (window.self !== window.top) throw new Error("Ouvrez Simbel dans un nouvel onglet pour vous connecter.");
  window.location.assign(url);
}
